import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, input, signal, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SearchFieldComponent } from '../../../../shared/components/search-field/search-field.component';
import { StatusLabelComponent } from '../../../../shared/components/status-label/status-label.component';
import { TableComponent } from '../../../../shared/components/table/table.component';
import { DatabaseService } from '../../../../core/services/database.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { StatusModalService } from '../../../../core/services/status-modal.service';
import { WebsocketService } from '../../../../core/services/websocket.service';
import { WebsiteData } from '../../../../shared/models/website-overview.model';
import { Database, DatabaseStatus, DatabaseUser } from '../../../../shared/models/database.models';
import { PageEvent } from '@angular/material/paginator';
import { Status } from '../../../../shared/models/status-label.model';
import { AddDatabaseDialogComponent } from '../../dialogs/add-database-dialog/add-database-dialog.component';
import { SnackbarComponent } from '../../../../shared/components/snackbar/snackbar.component';
import { AddDatabaseUserDialogComponent } from '../../dialogs/add-database-user-dialog/add-database-user-dialog.component';
import { ConfirmationDialogComponent } from '../../../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ChangePasswordDialogComponent } from '../../dialogs/change-password-dialog/change-password-dialog.component';

@Component({
  selector: 'app-databases',
  imports: [
    MatCardModule,
    FontAwesomeModule,
    MatSlideToggleModule,
    SearchFieldComponent,
    TableComponent,
    StatusLabelComponent,
    MatTableModule,
    MatTooltipModule,
    AsyncPipe,
    MatButtonModule,
  ],
  templateUrl: './databases.component.html',
  styleUrl: './databases.component.scss',
})
export class DatabasesComponent implements OnDestroy, OnInit {
  private databaseService = inject(DatabaseService);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  private websocketService = inject(WebsocketService);

  websiteData = input.required<WebsiteData>();

  website = computed(() => this.websiteData().website);

  website$ = toObservable(this.website);

  refresh$ = new BehaviorSubject<void>(undefined);

  databases$ = this.refresh$.pipe(
    switchMap(() => this.website$),
    switchMap((w) => this.databaseService.getByWebsiteId(w.id)),
    shareReplay(1),
  );

  filters = signal<{ page: number; pageSize: number; searchText?: string }>({
    page: 1,
    pageSize: 10,
  });

  filters$ = toObservable(this.filters);

  filteredDatabases$ = combineLatest([this.databases$, this.filters$]).pipe(
    map(([databases, filters]) => {
      const records = databases ?? [];

      // Search
      const filteredRecords = filters.searchText
        ? records.filter((record) =>
            record.name.toLowerCase().includes(filters.searchText!.toLowerCase()),
          )
        : records;

      // Pagination
      const start = (filters.page - 1) * filters.pageSize;
      const end = start + filters.pageSize;

      return filteredRecords.slice(start, end);
    }),
  );

  destroyed$ = new Subject();

  databasesColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: Database) => element.id.toString(),
    },
    {
      columnDef: 'name',
      header: 'Database Name',
      cell: (element: Database) => `${element.name}`,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'size',
      header: 'Size',
      cell: (element: Database) => element.size.toString(),
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  databaseUsersColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: DatabaseUser) => element.id.toString(),
    },
    {
      columnDef: 'type',
      header: 'Type',
      cell: (element: DatabaseUser) => (element.unixUser ? 'Main' : 'DB User'),
    },
    {
      columnDef: 'name',
      header: 'Database User',
      cell: (element: DatabaseUser) => `${element.username}`,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  ngOnInit(): void {
    this.websocketService
      .listen<{ title: string; body: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(
          ({ title, body }) =>
            body === `Database for website with id ${this.website().id} created successfully.` ||
            title === 'Database Deleted!' ||
            title === 'Database User Created' ||
            title === 'Database User Deleted',
        ),
      )
      .subscribe(() => {
        this.refresh$.next();
      });
  }

  onSearch(text: string) {
    this.filters.update((f) => ({ ...f, searchText: text }));
  }

  handlePageEvent(event: PageEvent) {
    this.filters.update((f) => ({ ...f, page: event.pageIndex + 1, pageSize: event.pageSize }));
  }

  addDatabase(): void {
    const server = this.websiteData().server?.id;
    if (server)
      this.dialog
        .open(AddDatabaseDialogComponent, {
          data: {
            subTitle: this.website().domain,
            websiteId: this.website().id,
            server,
          },
        })
        .afterClosed()
        .pipe(
          filter(Boolean),
          switchMap((data) => this.databaseService.createDatabase(data)),
        )
        .subscribe({
          next: () => {
            this.snackbar.openFromComponent(SnackbarComponent, {
              data: {
                text: `Database creation started!`,
              },
            });
          },
          error: (error) => {
            this.statusModalService.open({
              state: 'error',
              message: error.message,
            });
          },
        });
  }

  addDatabaseUser(databaseId: number): void {
    this.dialog
      .open(AddDatabaseUserDialogComponent, {
        data: { subTitle: this.website().domain, databaseId },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data) => this.databaseService.createUser(data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Database user creation started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  editDatabaseUser(user: DatabaseUser, databaseId: number): void {
    this.dialog
      .open(ChangePasswordDialogComponent, { data: { subTitle: user.username } })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data) => this.databaseService.editUser(user.id, { ...data, databaseId })),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Database deletion started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  deleteDatabase(database: Database): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Database',
          paragraphs: [
            `This action will permanently delete the database ${database.name}.`,
            'If this database is used by the site, deleting it will cause downtime.',
            `Are you sure you want to delete the database ${database.name}?`,
          ],
          primaryButtonText: 'Delete Database',
          type: 'delete',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.databaseService.deleteDatabase(database.id)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Database deletion started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  deleteDatabaseUser(user: DatabaseUser): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Database User',
          paragraphs: [
            `This action will permanently delete database user ${user.username} and revoke its access to all assigned databases.`,
            "If this database user is used in your website's database configuration, the website may stop working until the database credentials are updated.",
            `Are you sure you want to delete database user ${user.username}?`,
          ],
          primaryButtonText: 'Delete Database User',
          type: 'delete',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.databaseService.deleteUser(user.id)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Database user deletion started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  toStatus(key: DatabaseStatus): Status {
    switch (key) {
      case DatabaseStatus.ACTIVE:
      case DatabaseStatus.DONE:
        return Status.ACTIVE;
      case DatabaseStatus.IN_PROGRESS:
        return Status.IN_PROGRESS;
      case DatabaseStatus.DELETING:
        return Status.PENDING;
      case DatabaseStatus.DISABLED:
        return Status.DISABLED;
      default:
        return Status.ERROR;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
